/**
 * In-App Assessment Component - Paginated Maturity Assessment Flow
 * 
 * Features:
 * - Individual question screens (11 questions, one per screen)
 * - Automatic progression after answer selection (1-second delay)
 * - Implementation blockers selection screen
 * - Contact information collection with FTE count
 * - Visual progress tracking and step indicators
 * - Auto-save functionality with state persistence
 * 
 * Flow: Questions 1-11 → Blockers → Contact Form → Submission
 */

import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useLocation, useRoute } from 'wouter'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { Building2, Send, ArrowLeft, ArrowRight } from 'lucide-react'
import { maturityQuestions } from '@/constants/maturityQuestions'
import { apiRequest } from '@/lib/queryClient'
import {ProjectPayload} from "@shared/interfaces/projectPayload.interface.ts";

// Transform maturity questions from constants into an assessment format
const transformedQuestions = maturityQuestions.map((q, index) => ({
  id: index + 1,
  text: q.title,
  section: q.section,
  options: q.options.map((option, optIndex) => ({
    label: `Level ${optIndex + 1}`,
    description: option,
    value: optIndex + 1
  }))
}))

// Type definitions for assessment data structures
interface ContactForm {
  contactName: string
  contactEmail: string
  contactCompany: string
  contactRole: string
  fteCount: string    // Full-time employees working on R2R processes
}

interface AssessmentData {
  answers: number[]         // Array of selected option values (1-4 for each question)
  blockers: string[]        // Selected implementation blockers
  otherBlockers: string     // Custom blocker text
  contactInfo: ContactForm
}

// Pre-defined implementation blocker options
const blockersOptions = [
  "Limited technology infrastructure",
  "Resistance to change from stakeholders", 
  "Insufficient budget for transformation",
  "Lack of skilled personnel",
  "Regulatory compliance requirements",
  "Data quality and integration challenges",
  "Vendor dependency and lock-in",
  "Complex organizational structure",
  "Legacy system constraints",
  "Time constraints and competing priorities"
]

export default function InAppAssessment() {
  const [, setLocation] = useLocation()
  const [, params] = useRoute('/projects/:projectId/assessment')
  const { toast } = useToast()
  const queryClient = useQueryClient()
  
  // State management for paginated flow
  const [currentStep, setCurrentStep] = useState(0) // 0-10 = questions, 11 = blockers, 12 = contact
  const [showAutoProgress, setShowAutoProgress] = useState(false) // Visual feedback during auto-advance
  
  const [assessmentData, setAssessmentData] = useState<AssessmentData>({
    answers: Array(11).fill(0),
    blockers: [],
    otherBlockers: '',
    contactInfo: {
      contactName: '',
      contactEmail: '',
      contactCompany: '',
      contactRole: '',
      fteCount: ''
    }
  })

  const projectId = params?.projectId

  // Fetch project details (use default queryFn which injects Authorization header)
  const { data: project, isLoading: projectLoading } = useQuery<ProjectPayload>({
    queryKey: projectId ? [`/api/projects/${projectId}`] : [],
    enabled: !!projectId,
  })

  // Prefill from latest maturity results if present
  const { data: maturityHistory } = useQuery<any[]>({
    queryKey: projectId ? [`/api/maturity/${projectId}`] : [],
    enabled: !!projectId,
  })
  const prefillDone = useRef(false)
  const [existingResultId, setExistingResultId] = useState<string | null>(null)

  useEffect(() => {
    if (prefillDone.current) return;
    const latest = Array.isArray(maturityHistory) && maturityHistory.length > 0 ? maturityHistory[0] : null;
    if (!latest) return;
    try {
      if (latest.id) setExistingResultId(String(latest.id))
      const answers: number[] = Array.isArray(latest.data) ? latest.data : [];
      const paddedAnswers = Array(11).fill(0).map((_, i) => answers[i] || 0);
      setAssessmentData({
        answers: paddedAnswers,
        blockers: Array.isArray(latest.blockers) ? latest.blockers : [],
        otherBlockers: latest.other_blockers || '',
        contactInfo: {
          contactName: latest.contact_name || '',
          contactEmail: latest.contact_email || '',
          contactCompany: latest.contact_company || '',
          contactRole: latest.contact_role || '',
          fteCount: latest.fte_count || ''
        }
      })
      prefillDone.current = true
    } catch {}
  }, [maturityHistory])

  // Ensure company is sourced from project client_org and not user-editable
  useEffect(() => {
    if (!project || !project.client_org) return;
    setAssessmentData(prev => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo,
        contactCompany: project.client_org || prev.contactInfo.contactCompany,
      }
    }));
  }, [project?.client_org]);

  // Submit assessment mutation
  const submitAssessmentMutation = useMutation({
    mutationFn: async (data: AssessmentData) => {
      if (existingResultId) {
        // Update existing maturity result for this project
        return apiRequest(`/api/maturity/${existingResultId}`, {
          method: 'PUT',
          body: {
            data: data.answers,
            blockers: data.blockers,
            other_blockers: data.otherBlockers,
            completion_percentage: 100,
            contact_name: data.contactInfo.contactName,
            contact_email: data.contactInfo.contactEmail,
            contact_company: data.contactInfo.contactCompany,
            contact_role: data.contactInfo.contactRole,
            fte_count: data.contactInfo.fteCount,
          }
        })
      }
      // Create new if none exists yet
      return apiRequest('/api/maturity', {
        method: 'POST',
        body: {
          project_id: projectId,
          answers: data.answers,
          blockers: data.blockers,
          other_blockers: data.otherBlockers,
          contactName: data.contactInfo.contactName,
          contactEmail: data.contactInfo.contactEmail,
          contactCompany: data.contactInfo.contactCompany,
          contactRole: data.contactInfo.contactRole,
          fteCount: data.contactInfo.fteCount
        }
      })
    },
    onSuccess: () => {
      // Invalidate relevant caches so results page fetches fresh data
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] })
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: [`/api/maturity/${projectId}`] })
      }
      toast({
        title: "Assessment submitted",
        description: "Your maturity assessment has been completed successfully."
      })
      setLocation(`/projects/${projectId}/maturity-results`)
    },
    onError: () => {
      toast({
        title: "Submission Failed", 
        description: "Please try again or contact support.",
        variant: "destructive"
      })
    }
  })

  /**
   * Updates answer for current question and triggers auto-progression
   * @param questionIndex - Index of the question being answered
   * @param value - Selected option value (1-4)
   */
  const updateAnswer = (questionIndex: number, value: number) => {
    setAssessmentData(prev => ({
      ...prev,
      answers: prev.answers.map((answer, index) => 
        index === questionIndex ? value : answer
      )
    }))
    
    // Auto-progress to the next screen after answer selection
    if (currentStep < 10) {
      // Move to the next question
      setShowAutoProgress(true)
      setTimeout(() => {
        setCurrentStep(prev => prev + 1)
        setShowAutoProgress(false)
      }, 1000) // 1-second delay for user to see their selection
    } else if (currentStep === 10) {
      // Last question completed, move to blockers screen
      setShowAutoProgress(true)
      setTimeout(() => {
        setCurrentStep(11)
        setShowAutoProgress(false)
      }, 1000)
    }
  }

  const toggleBlocker = (blocker: string) => {
    setAssessmentData(prev => ({
      ...prev,
      blockers: prev.blockers.includes(blocker)
        ? prev.blockers.filter(b => b !== blocker)
        : [...prev.blockers, blocker]
    }))
  }

  const updateContactInfo = (field: keyof ContactForm, value: string) => {
    setAssessmentData(prev => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo,
        [field]: value
      }
    }))
  }

  const nextStep = () => {
    if (currentStep <= 10) {
      // For question screens, validate current question is answered
      if (assessmentData.answers[currentStep] === 0) {
        toast({
          title: "Please Select an Answer",
          description: "Choose an option before continuing.",
          variant: "destructive"
        })
        return
      }
    }
    
    if (currentStep < 12) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }

  const handleSubmit = () => {
    if (submitAssessmentMutation.isPending) return;
    // Validate all questions answered
    const unanswered = assessmentData.answers.some(answer => answer === 0)
    if (unanswered) {
      toast({
        title: "Incomplete Assessment",
        description: "Please answer all assessment questions before submitting.",
        variant: "destructive"
      })
      return
    }

    // Validate required contact fields
    const { contactName, contactEmail, contactCompany, contactRole, fteCount } = assessmentData.contactInfo
    if (!contactName || !contactEmail || !contactCompany || !contactRole || !fteCount) {
      toast({
        title: "Missing Information",
        description: "Please fill in all contact information fields.",
        variant: "destructive"
      })
      return
    }

    submitAssessmentMutation.mutate(assessmentData)
  }

  if (projectLoading) {
    return (
      <div className="min-h-screen bg-[#0D1224] p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#0D1224] p-6 flex items-center justify-center">
        <Card className="bg-gray-800 border-gray-700 max-w-md">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-bold text-white mb-4">Project Not Found</h2>
            <p className="text-gray-400">The project you're trying to access doesn't exist.</p>
            <Button 
              onClick={() => setLocation('/')} 
              className="mt-4 bg-primary hover:bg-primaryHover text-forestBlack"
            >
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const completedQuestions = assessmentData.answers.filter(answer => answer > 0).length
  const progressPercent = Math.round((completedQuestions / transformedQuestions.length) * 100)

  return (
    <div className="min-h-screen bg-gradient-to-br from-forestBlack via-[#04414D] to-[#334155] text-foreground p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <Building2 className="w-8 h-8 text-primary" />
                <div>
                  <h1 className="text-2xl font-bold text-white">{project.name}</h1>
                  <p className="text-gray-400">Maturity Assessment</p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setLocation('/')}
                className="border-gray-600 text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
            
            <Progress value={progressPercent} className="h-2" />
            <p className="text-sm text-gray-400 mt-2">
              {currentStep <= 10 
                ? `Question ${currentStep + 1} of ${transformedQuestions.length} • ${completedQuestions} completed (${progressPercent}%)`
                : currentStep === 11 
                ? `Implementation Blockers • ${completedQuestions} of ${transformedQuestions.length} questions completed`
                : `Contact Information • ${completedQuestions} of ${transformedQuestions.length} questions completed`
              }
            </p>
          </CardContent>
        </Card>

        {/* Step Indicators */}
        <div className="flex items-center justify-center space-x-4">
          <div className={`flex items-center ${currentStep <= 10 ? 'text-primary' : completedQuestions === transformedQuestions.length ? 'text-primary' : 'text-redwoodGray'}`}>
            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
              currentStep <= 10 ? 'border-primary bg-primary text-black' : 
              completedQuestions === transformedQuestions.length ? 'border-primary' : 'border-redwoodGray'
            }`}>
              {completedQuestions === transformedQuestions.length ? '✓' : '1'}
            </div>
            <span className="ml-2 font-medium">Assessment</span>
          </div>
          
          <div className={`w-8 h-1 ${currentStep >= 11 ? 'bg-primary' : 'bg-redwoodGray'}`}></div>
          
          <div className={`flex items-center ${currentStep >= 11 ? 'text-primary' : 'text-redwoodGray'}`}>
            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
              currentStep >= 11 ? 'border-primary' : 'border-redwoodGray'
            } ${currentStep === 11 ? 'bg-primary text-forestBlack' : ''}`}>
              {currentStep > 11 ? '✓' : '2'}
            </div>
            <span className="ml-2 font-medium">Blockers</span>
          </div>
          
          <div className={`w-8 h-1 ${currentStep >= 12 ? 'bg-primary' : 'bg-redwoodGray'}`}></div>
          
          <div className={`flex items-center ${currentStep >= 12 ? 'text-primary' : 'text-redwoodGray'}`}>
            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
              currentStep >= 12 ? 'border-primary' : 'border-white'
            } ${currentStep === 12 ? 'bg-primary text-forestBlack' : ''}`}>
              3
            </div>
            <span className="ml-2 font-medium">Contact</span>
          </div>
        </div>

        {/* Individual Question Steps */}
        {currentStep <= 10 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-2">Assessment Question {currentStep + 1}</h2>
              <p className="text-gray-400">Select the option that best describes your organization</p>
            </div>
            
            {showAutoProgress && (
              <Card className="bg-primary/10 border-primary">
                <CardContent className="p-4 text-center">
                  <div className="flex items-center justify-center gap-2 text-primary">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    <span>Moving to next question...</span>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between mb-4">
                  <Badge variant="outline" className="border-primary text-primary">
                    {transformedQuestions[currentStep]?.section}
                  </Badge>
                  <span className="text-sm text-gray-400">{currentStep + 1} of {transformedQuestions.length}</span>
                </div>
                <CardTitle className="text-xl text-white mb-2">
                  {transformedQuestions[currentStep]?.text}
                </CardTitle>
                <CardDescription className="text-gray-300">
                  Choose the level that most accurately reflects your current state
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {transformedQuestions[currentStep]?.options.map((option, optionIndex) => {
                  const value = option.value
                  return (
                    <Label
                      key={optionIndex}
                      data-cy={`question-${currentStep}-${option.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        assessmentData.answers[currentStep] === value
                          ? 'border-primary bg-primary/10 text-white'
                          : 'border-gray-600 hover:border-redwoodGray text-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`question-${currentStep}`}
                        value={value}
                        checked={assessmentData.answers[currentStep] === value}
                        onChange={() => updateAnswer(currentStep, value)}
                        className="sr-only"
                        disabled={showAutoProgress}
                      />
                      <div className="flex-1">
                        <div className="font-medium mb-1 flex items-center gap-2">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs ${
                            assessmentData.answers[currentStep] === value
                              ? 'border-primary bg-primary text-black'
                              : 'border-redwoodGray'
                          }`}>
                            {value}
                          </div>
                          {option.label}
                        </div>
                        <div className="text-sm opacity-75 ml-8">{option.description}</div>
                      </div>
                    </Label>
                  )
                })}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Blockers Step */}
        {currentStep === 11 && (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-lg text-white">Implementation Blockers</CardTitle>
                <CardDescription className="text-gray-300">
                  What challenges or obstacles might prevent or slow down your close automation implementation?
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {blockersOptions.map((blocker, index) => (
                    <Label
                      key={index}
                      data-cy={`blocker-${blocker.toLowerCase().replace(/\s+/g, '-')}`}
                      className="flex items-center space-x-2 cursor-pointer text-gray-300"
                    >
                      <Checkbox
                        checked={assessmentData.blockers.includes(blocker)}
                        onCheckedChange={() => toggleBlocker(blocker)}
                      />
                      <span>{blocker}</span>
                    </Label>
                  ))}
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Other blockers or specific challenges:</Label>
                  <Textarea
                    data-cy="blockers-other"
                    value={assessmentData.otherBlockers}
                    onChange={(e) => setAssessmentData(prev => ({ ...prev, otherBlockers: e.target.value }))}
                    placeholder="Please describe any additional challenges or blockers specific to your organization..."
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Contact Information Step */}
        {currentStep === 12 && (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-lg text-white">Contact Information</CardTitle>
                <CardDescription className="text-gray-300">
                  Please provide contact details to complete the assessment.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Full Name *</Label>
                    <Input
                      data-cy="contact-name"
                      value={assessmentData.contactInfo.contactName}
                      onChange={(e) => updateContactInfo('contactName', e.target.value)}
                      placeholder="Your full name"
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Email Address *</Label>
                    <Input
                      type="email"
                      data-cy="contact-email"
                      value={assessmentData.contactInfo.contactEmail}
                      onChange={(e) => updateContactInfo('contactEmail', e.target.value)}
                      placeholder="your.email@company.com"
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Company/Organization *</Label>
                    <Input
                      data-cy="contact-company"
                      value={project?.client_org || assessmentData.contactInfo.contactCompany}
                      disabled
                      readOnly
                      placeholder="Your organization name"
                      className="bg-gray-700 border-gray-600 text-white opacity-80 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-400">Sourced from project settings (client organization)</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Job Title/Role *</Label>
                    <Input
                      data-cy="contact-role"
                      value={assessmentData.contactInfo.contactRole}
                      onChange={(e) => updateContactInfo('contactRole', e.target.value)}
                      placeholder="e.g., Finance Director, Controller"
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-white">R2R Team Size (FTE Count) *</Label>
                    <Input
                      data-cy="contact-fte"
                      value={assessmentData.contactInfo.fteCount}
                      onChange={(e) => updateContactInfo('fteCount', e.target.value)}
                      placeholder="e.g., 15 FTEs"
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                    <p className="text-sm text-gray-400">
                      Please provide the number of full-time employees currently working on Record-to-Report processes
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation Buttons */}
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 0 || showAutoProgress}
                className="border-gray-600 text-white hover:bg-gray-700"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>

              {currentStep <= 10 ? (
                <div className="flex items-center gap-4 ml-auto text-right">
                  <span className="text-sm text-gray-400">
                    {assessmentData.answers[currentStep] > 0
                      ? 'Answer already selected — update it or click Next to continue'
                      : 'Select an answer to continue automatically'}
                  </span>
                  <Button
                    data-cy="next-question"
                    onClick={nextStep}
                    disabled={assessmentData.answers[currentStep] === 0 || showAutoProgress}
                    className={`bg-primary hover:bg-primaryHover text-forestBlack ${assessmentData.answers[currentStep] === 0 ? 'opacity-50' : ''}`}
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              ) : currentStep === 11 ? (
                <Button
                  data-cy="continue-to-contact"
                  onClick={nextStep}
                  className="bg-primary hover:bg-primaryHover text-forestBlack"
                >
                  Continue to Contact Info
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  data-cy="submit-assessment"
                  onClick={handleSubmit}
                  disabled={submitAssessmentMutation.isPending}
                  className="bg-primary hover:bg-primaryHover text-forestBlack px-8"
                >
                  {submitAssessmentMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-forestBlack mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Assessment
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
